/**
 * roleConfig.js — Single source of truth for Field Notes role system
 * Powered by Cold Harbor Digital
 *
 * Every role, permission, route, and nav config lives here.
 * All other files import from this — never hardcode role strings elsewhere.
 */

// ─── Role Definitions ──────────────────────────────────────────────
export const ROLES = {
  AGENT: "agent",
  CLINICIAN: "clinician",
  PRESCRIBER: "prescriber",
  FRONTDESK: "frontdesk",
  BILLING: "billing",
  SUPERVISOR: "supervisor",
  ADMIN: "admin",
};

// ─── Role Metadata (display names, descriptions) ───────────────────
export const ROLE_META = {
  [ROLES.AGENT]: {
    label: "Community Agent",
    short: "Agent",
    description: "ICM/PSR/CST — field sessions, voice notes, caseload management",
    color: "#63b3ed",
    icon: "UserCheck",
  },
  [ROLES.CLINICIAN]: {
    label: "Clinician",
    short: "Clinician",
    description: "Therapists, counselors — therapy sessions, treatment plans, assessments",
    color: "#68d391",
    icon: "Stethoscope",
  },
  [ROLES.PRESCRIBER]: {
    label: "Prescriber",
    short: "Prescriber",
    description: "Psychiatrists, APRNs — medication management, psychiatric evaluations",
    color: "#b794f4",
    icon: "Pill",
  },
  [ROLES.FRONTDESK]: {
    label: "Front Desk",
    short: "Front Desk",
    description: "Intake coordinators — scheduling, referrals, insurance verification",
    color: "#f6ad55",
    icon: "ClipboardList",
  },
  [ROLES.BILLING]: {
    label: "Billing Specialist",
    short: "Billing",
    description: "Claims, denials, payment posting, authorization tracking",
    color: "#4fd1c5",
    icon: "DollarSign",
  },
  [ROLES.SUPERVISOR]: {
    label: "Supervisor",
    short: "Supervisor",
    description: "Co-sign notes, safety review, compliance, team management",
    color: "#ecc94b",
    icon: "Shield",
  },
  [ROLES.ADMIN]: {
    label: "Administrator",
    short: "Admin",
    description: "Full system access — org settings, billing config, role management, reports",
    color: "#fc8181",
    icon: "Settings",
  },
};

// ─── Permission Sets ────────────────────────────────────────────────
// Granular permissions that can be checked with hasPermission()
export const PERMISSIONS = {
  // Caseload
  VIEW_OWN_CASELOAD: "view_own_caseload",
  VIEW_SUPERVISED_CASELOADS: "view_supervised_caseloads", // supervisor sees only their supervisees
  VIEW_ALL_CASELOADS: "view_all_caseloads",               // admin sees everything
  MANAGE_CASELOAD_ASSIGNMENT: "manage_caseload_assignment",

  // Sessions & Notes
  RECORD_SESSION: "record_session",
  CREATE_NOTE: "create_note",
  EDIT_OWN_NOTES: "edit_own_notes",
  VIEW_SHARED_INDIVIDUAL_NOTES: "view_shared_individual_notes", // see other providers' notes on shared individuals
  VIEW_SUPERVISED_NOTES: "view_supervised_notes",               // supervisor sees supervisees' notes
  VIEW_ALL_NOTES: "view_all_notes",                             // admin sees all
  COSIGN_NOTES: "cosign_notes",
  REQUEST_REVISION: "request_revision",

  // Clinical
  CREATE_TREATMENT_PLAN: "create_treatment_plan",
  EDIT_TREATMENT_PLAN: "edit_treatment_plan",
  VIEW_TREATMENT_PLAN: "view_treatment_plan",
  ADMINISTER_ASSESSMENT: "administer_assessment",
  MANAGE_DIAGNOSES: "manage_diagnoses",
  MANAGE_MEDICATIONS: "manage_medications",

  // Intake & Referrals
  CREATE_INTAKE: "create_intake",
  MANAGE_REFERRALS: "manage_referrals",
  MANAGE_CONSENTS: "manage_consents",
  DISCHARGE_INDIVIDUAL: "discharge_individual",

  // Scheduling
  VIEW_OWN_SCHEDULE: "view_own_schedule",
  VIEW_SUPERVISED_SCHEDULES: "view_supervised_schedules",
  VIEW_ALL_SCHEDULES: "view_all_schedules",
  MANAGE_SCHEDULING: "manage_scheduling",

  // Billing
  VIEW_BILLING: "view_billing",
  MANAGE_CLAIMS: "manage_claims",
  MANAGE_AUTHORIZATIONS: "manage_authorizations",
  CONFIGURE_BILLING: "configure_billing",

  // Safety & Compliance
  VIEW_SAFETY_ALERTS: "view_safety_alerts",
  MANAGE_SAFETY_ALERTS: "manage_safety_alerts",
  VIEW_COMPLIANCE: "view_compliance",

  // Admin
  MANAGE_TEAM: "manage_team",
  MANAGE_ROLES: "manage_roles",
  VIEW_REPORTS: "view_reports",
  MANAGE_ORG_SETTINGS: "manage_org_settings",
  VIEW_AUDIT_LOG: "view_audit_log",
};

// ─── Role → Permission Mapping ─────────────────────────────────────
const P = PERMISSIONS;

export const ROLE_PERMISSIONS = {
  [ROLES.AGENT]: [
    P.VIEW_OWN_CASELOAD,
    P.RECORD_SESSION,
    P.CREATE_NOTE,
    P.EDIT_OWN_NOTES,
    P.VIEW_SHARED_INDIVIDUAL_NOTES,  // can see other providers' notes on shared individuals
    P.VIEW_TREATMENT_PLAN,           // read-only
    P.VIEW_OWN_SCHEDULE,
    P.VIEW_SAFETY_ALERTS,
  ],

  [ROLES.CLINICIAN]: [
    P.VIEW_OWN_CASELOAD,
    P.RECORD_SESSION,
    P.CREATE_NOTE,
    P.EDIT_OWN_NOTES,
    P.VIEW_SHARED_INDIVIDUAL_NOTES,  // can see agent/prescriber notes on shared individuals
    P.CREATE_TREATMENT_PLAN,
    P.EDIT_TREATMENT_PLAN,
    P.VIEW_TREATMENT_PLAN,
    P.ADMINISTER_ASSESSMENT,
    P.MANAGE_DIAGNOSES,
    P.VIEW_OWN_SCHEDULE,
    P.VIEW_SAFETY_ALERTS,
    P.DISCHARGE_INDIVIDUAL,
  ],

  [ROLES.PRESCRIBER]: [
    P.VIEW_OWN_CASELOAD,
    P.RECORD_SESSION,
    P.CREATE_NOTE,
    P.EDIT_OWN_NOTES,
    P.VIEW_SHARED_INDIVIDUAL_NOTES,  // can see clinician/agent notes on shared individuals
    P.CREATE_TREATMENT_PLAN,
    P.EDIT_TREATMENT_PLAN,
    P.VIEW_TREATMENT_PLAN,
    P.ADMINISTER_ASSESSMENT,
    P.MANAGE_DIAGNOSES,
    P.MANAGE_MEDICATIONS,
    P.VIEW_OWN_SCHEDULE,
    P.VIEW_SAFETY_ALERTS,
  ],

  [ROLES.FRONTDESK]: [
    P.CREATE_INTAKE,
    P.MANAGE_REFERRALS,
    P.MANAGE_CONSENTS,
    P.MANAGE_SCHEDULING,
    P.VIEW_ALL_SCHEDULES,
    P.VIEW_OWN_SCHEDULE,
  ],

  [ROLES.BILLING]: [
    P.VIEW_BILLING,
    P.MANAGE_CLAIMS,
    P.MANAGE_AUTHORIZATIONS,
    // Note: billing sees billing metadata only, NOT full clinical notes
  ],

  [ROLES.SUPERVISOR]: [
    P.VIEW_OWN_CASELOAD,
    P.VIEW_SUPERVISED_CASELOADS,     // only sees staff they supervise
    P.MANAGE_CASELOAD_ASSIGNMENT,
    P.RECORD_SESSION,
    P.CREATE_NOTE,
    P.EDIT_OWN_NOTES,
    P.VIEW_SHARED_INDIVIDUAL_NOTES,
    P.VIEW_SUPERVISED_NOTES,         // sees all notes from their supervisees
    P.COSIGN_NOTES,
    P.REQUEST_REVISION,
    P.CREATE_TREATMENT_PLAN,
    P.EDIT_TREATMENT_PLAN,
    P.VIEW_TREATMENT_PLAN,
    P.ADMINISTER_ASSESSMENT,
    P.MANAGE_DIAGNOSES,
    P.MANAGE_MEDICATIONS,
    P.CREATE_INTAKE,
    P.MANAGE_REFERRALS,
    P.MANAGE_CONSENTS,
    P.DISCHARGE_INDIVIDUAL,
    P.VIEW_OWN_SCHEDULE,
    P.VIEW_SUPERVISED_SCHEDULES,     // sees supervisees' schedules
    P.MANAGE_SCHEDULING,
    P.VIEW_BILLING,
    P.VIEW_SAFETY_ALERTS,
    P.MANAGE_SAFETY_ALERTS,
    P.VIEW_COMPLIANCE,
    P.MANAGE_TEAM,
    P.MANAGE_ROLES,
    P.VIEW_REPORTS,
  ],

  [ROLES.ADMIN]: [
    // Admin gets everything
    ...Object.values(PERMISSIONS),
  ],
};

// ─── Route Access Control ──────────────────────────────────────────
// Which roles can access which route groups
export const ROUTE_ACCESS = {
  // Agent routes
  "/dashboard": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/caseload": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/session": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/notes": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.BILLING, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/schedule": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.FRONTDESK, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/analytics": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/individual": [ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/settings": Object.values(ROLES), // everyone

  // Role-specific dashboards
  "/clinician": [ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/prescriber": [ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/frontdesk": [ROLES.FRONTDESK, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/billing-dashboard": [ROLES.BILLING, ROLES.SUPERVISOR, ROLES.ADMIN],

  // Admin routes
  "/admin": [ROLES.SUPERVISOR, ROLES.ADMIN],

  // Intake & referrals
  "/intake": [ROLES.FRONTDESK, ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN],
  "/referrals": [ROLES.FRONTDESK, ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN],

  // Assessments
  "/assessments": [ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN],
};

// ─── Navigation Config Per Role ────────────────────────────────────
// Each role gets a tailored bottom nav
export const NAV_CONFIG = {
  [ROLES.AGENT]: [
    { id: "home", icon: "Home", label: "Home", path: "/dashboard" },
    { id: "caseload", icon: "Users", label: "Caseload", path: "/caseload" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule", accent: true },
    { id: "notes", icon: "ClipboardList", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.CLINICIAN]: [
    { id: "home", icon: "Home", label: "Home", path: "/dashboard" },
    { id: "caseload", icon: "Users", label: "Clients", path: "/caseload" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule", accent: true },
    { id: "notes", icon: "FileText", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.PRESCRIBER]: [
    { id: "home", icon: "Home", label: "Home", path: "/dashboard" },
    { id: "caseload", icon: "Users", label: "Patients", path: "/caseload" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule", accent: true },
    { id: "notes", icon: "FileText", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.FRONTDESK]: [
    { id: "home", icon: "Home", label: "Home", path: "/frontdesk" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule" },
    { id: "intake", icon: "UserPlus", label: "Intake", path: "/intake", accent: true },
    { id: "referrals", icon: "ClipboardList", label: "Referrals", path: "/referrals" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.BILLING]: [
    { id: "home", icon: "Home", label: "Home", path: "/billing-dashboard" },
    { id: "claims", icon: "DollarSign", label: "Claims", path: "/billing-dashboard?tab=claims" },
    { id: "auths", icon: "Shield", label: "Auths", path: "/billing-dashboard?tab=auths", accent: true },
    { id: "notes", icon: "FileText", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.SUPERVISOR]: [
    { id: "home", icon: "Home", label: "Home", path: "/dashboard" },
    { id: "admin", icon: "Shield", label: "Admin", path: "/admin" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule", accent: true },
    { id: "notes", icon: "FileText", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],

  [ROLES.ADMIN]: [
    { id: "home", icon: "Home", label: "Home", path: "/dashboard" },
    { id: "admin", icon: "Shield", label: "Admin", path: "/admin" },
    { id: "schedule", icon: "Calendar", label: "Schedule", path: "/schedule", accent: true },
    { id: "notes", icon: "FileText", label: "Notes", path: "/notes" },
    { id: "settings", icon: "Settings", label: "Settings", path: "/settings" },
  ],
};

// ─── Dashboard Routing ─────────────────────────────────────────────
// Where each role lands after login
export const DASHBOARD_ROUTES = {
  [ROLES.AGENT]: "/dashboard",
  [ROLES.CLINICIAN]: "/dashboard",
  [ROLES.PRESCRIBER]: "/dashboard",
  [ROLES.FRONTDESK]: "/frontdesk",
  [ROLES.BILLING]: "/billing-dashboard",
  [ROLES.SUPERVISOR]: "/dashboard",
  [ROLES.ADMIN]: "/dashboard",
};

// ─── Helper Functions ──────────────────────────────────────────────

/**
 * Check if a role has a specific permission
 */
export function roleHasPermission(role, permission) {
  const perms = ROLE_PERMISSIONS[role];
  if (!perms) return false;
  return perms.includes(permission);
}

/**
 * Check if a role can access a given route
 */
export function roleCanAccessRoute(role, path) {
  // Strip dynamic segments: /individual/abc123 → /individual
  const basePath = "/" + path.split("/").filter(Boolean)[0];
  const allowedRoles = ROUTE_ACCESS[basePath];
  if (!allowedRoles) return true; // unlisted routes default to open (protected by auth)
  return allowedRoles.includes(role);
}

/**
 * Get nav items for a role
 */
export function getNavForRole(role) {
  return NAV_CONFIG[role] || NAV_CONFIG[ROLES.AGENT];
}

/**
 * Get the home/landing route for a role
 */
export function getDashboardRoute(role) {
  return DASHBOARD_ROUTES[role] || "/dashboard";
}

/**
 * Check if role is a clinical role (can view/manage clinical data)
 */
export function isClinicalRole(role) {
  return [ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN].includes(role);
}

/**
 * Check if role is a supervisory role
 */
export function isSupervisoryRole(role) {
  return [ROLES.SUPERVISOR, ROLES.ADMIN].includes(role);
}

/**
 * Get all roles as array for dropdowns
 */
export function getAllRoles() {
  return Object.values(ROLES).map((role) => ({
    value: role,
    ...ROLE_META[role],
  }));
}

// ─── Data Model: User Profile Fields ────────────────────────────────
// These document the expected fields on user profile documents.
// Used by authService.js when creating/updating profiles.
//
// ALL roles:
//   uid, email, displayName, firstName, lastName, role, organization,
//   credentials, employeeId, program, createdAt, lastLogin,
//   supervisorId  ← uid of direct supervisor (one supervisor per staff)
//
// CLINICIAN / PRESCRIBER / SUPERVISOR additionally:
//   licenseType, licenseNumber, licenseState, specialty, npiNumber
//
// PRESCRIBER additionally:
//   deaNumber

// ─── Data Model: Individual (sharedIndividuals) ────────────────────
// Multi-provider assignment model. An individual can have:
//
//   assignedAgent:       uid (primary community agent)
//   assignedAgentName:   string
//   assignedClinician:   uid (therapist/counselor)
//   assignedClinicianName: string
//   assignedPrescriber:  uid (psychiatrist/APRN)
//   assignedPrescriberName: string
//
// Legacy fields kept for backward compatibility:
//   assignedAgents:      [uid, ...]  (array — existing data)
//   assignedAgentNames:  [name, ...]
//   primaryAgent:        uid
//   primaryAgentName:    string
//
// Note visibility rules:
//   - You always see your own notes
//   - If you share an individual with another provider, you can see their
//     notes ON THAT INDIVIDUAL (agent sees clinician notes, clinician sees
//     agent notes, prescriber sees both)
//   - Supervisors see all notes from staff where staff.supervisorId === supervisor.uid
//   - Admin sees everything in the org

// ─── Supervisor Scope Helper ────────────────────────────────────────

/**
 * Check if a supervisor should have access to a staff member's data.
 * Returns true if:
 *   - The staff member's supervisorId matches the supervisor's uid
 *   - The user is an admin (sees everything)
 *
 * @param {Object} supervisorProfile - The supervisor's profile { id, role }
 * @param {Object} staffProfile - The staff member's profile { supervisorId }
 */
export function canSupervise(supervisorProfile, staffProfile) {
  if (!supervisorProfile || !staffProfile) return false;
  if (supervisorProfile.role === ROLES.ADMIN) return true;
  if (supervisorProfile.role === ROLES.SUPERVISOR) {
    return staffProfile.supervisorId === supervisorProfile.id;
  }
  return false;
}

/**
 * Check if a user can see notes on a specific individual.
 * Returns true if:
 *   - User is assigned to the individual (any provider role)
 *   - User supervises someone assigned to the individual
 *   - User is admin
 *
 * @param {Object} userProfile - { id, role, ... }
 * @param {Object} individual - { assignedAgent, assignedClinician, assignedPrescriber, ... }
 * @param {Array} teamMembers - all team members (to check supervisorId chains)
 */
export function canViewIndividualNotes(userProfile, individual, teamMembers = []) {
  if (!userProfile || !individual) return false;

  const uid = userProfile.id;
  const role = userProfile.role;

  // Admin sees all
  if (role === ROLES.ADMIN) return true;

  // Direct assignment — any provider type
  if (individual.assignedAgent === uid) return true;
  if (individual.assignedClinician === uid) return true;
  if (individual.assignedPrescriber === uid) return true;
  // Legacy array check
  if ((individual.assignedAgents || []).includes(uid)) return true;

  // Supervisor — check if any assigned provider is their supervisee
  if (role === ROLES.SUPERVISOR) {
    const assignedUids = [
      individual.assignedAgent,
      individual.assignedClinician,
      individual.assignedPrescriber,
      ...(individual.assignedAgents || []),
    ].filter(Boolean);

    return teamMembers.some(
      (member) => assignedUids.includes(member.id) && member.supervisorId === uid
    );
  }

  return false;
}